import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { MEDIA_VIDEOS } from "@/data/mediaVideos";
import { clock, thumbWide } from "@/lib/mediaFormat";

/** videos in view at each width: five on a desktop, one on a phone */
function usePerView() {
  const pick = () => {
    if (typeof window === "undefined") return 5;
    const w = window.innerWidth;
    return w >= 1024 ? 5 : w >= 768 ? 3 : w >= 640 ? 2 : 1;
  };
  const [perView, setPerView] = useState(pick);
  useEffect(() => {
    const onResize = () => setPerView(pick());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return perView;
}

const ARROW =
  "absolute top-[40%] z-10 -translate-y-1/2 rounded-full bg-white/90 p-1.5 text-primary shadow-lg transition-all hover:scale-110 hover:bg-white sm:p-2 md:p-3";

/**
 * MyVaidyam's videos (data/mediaVideos) in a slider like the centre pages'
 * Facilities & Amenities one, each opening its video page. Shown above the
 * footer on every centre page. The arrows (or a swipe) turn a whole view at a
 * time: five videos, then the next five, on a desktop; one on a phone.
 */
export default function MediaVideoSlider() {
  const perView = usePerView();
  const pages = Math.ceil(MEDIA_VIDEOS.length / perView);
  const [at, setAt] = useState(0);
  const page = Math.min(at, pages - 1);
  const go = (i: number) => setAt((i + pages) % pages);
  // the first video in view: a short last view still shows a full row
  const first = Math.min(page * perView, MEDIA_VIDEOS.length - perView);
  const [touchX, setTouchX] = useState<number | null>(null);

  return (
    <section data-free-wrap className="container mx-auto px-3 md:px-4 pt-12 pb-10 md:pt-16 md:pb-14 [&_li]:[text-wrap:wrap]" aria-labelledby="media-video-slider">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 text-center md:mb-10">
          <h2 id="media-video-slider" className="mb-3 text-2xl font-bold text-primary md:text-4xl">
            MyVaidyam Videos
          </h2>
          <p className="mx-auto px-4 text-base md:text-lg" style={{ color: "#7F543D" }}>
            Videos from MyVaidyam's video channel on Ayurveda treatments and programmes in India. Choose a video to
            watch it and read more about the treatment.
          </p>
        </div>

        <div className="relative px-8 sm:px-10 md:px-14">
          <button type="button" onClick={() => go(page - 1)} className={`${ARROW} left-0 md:left-1`} aria-label="Previous videos">
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
          </button>
          <button type="button" onClick={() => go(page + 1)} className={`${ARROW} right-0 md:right-1`} aria-label="Next videos">
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
          </button>

          {/* only the videos in view show: the row is clipped between the arrows */}
          <div
            className="overflow-hidden"
            onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchX === null) return;
              const dx = e.changedTouches[0].clientX - touchX;
              if (Math.abs(dx) > 40) go(page + (dx < 0 ? 1 : -1));
              setTouchX(null);
            }}
          >
            <ul
              className="m-0 flex list-none p-0 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${(first * 100) / perView}%)` }}
            >
              {MEDIA_VIDEOS.map((v, i) => {
                const shown = i >= first && i < first + perView;
                return (
                  <li key={v.slug} className="flex-shrink-0 px-1 py-2 sm:px-2" style={{ width: `${100 / perView}%` }} aria-hidden={!shown || undefined}>
                    <Link
                      to={`/media/${v.slug}`}
                      tabIndex={shown ? undefined : -1}
                      className="group block h-full rounded-xl border border-primary/10 bg-white p-2 shadow-lg transition-all hover:border-primary/30"
                    >
                      <span className="relative block overflow-hidden rounded-lg">
                        <img
                          loading="lazy"
                          decoding="async"
                          src={thumbWide(v.id)}
                          alt=""
                          width={1280}
                          height={720}
                          className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#C24E00]/90 text-white shadow-lg">
                            <Play className="ml-0.5 h-5 w-5 fill-current" />
                          </span>
                        </span>
                        <span className="absolute bottom-1.5 right-1.5 rounded bg-black/75 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                          {clock(v.seconds)}
                        </span>
                      </span>
                      <span className="mt-2 block px-1 pb-1 text-center text-base font-semibold leading-snug text-primary group-hover:text-[#C24E00] sm:text-sm">
                        {v.heading}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
